import { createClient } from "@supabase/supabase-js";

import {
  INSURANCE_INTAKE_CONSENT_TEXT,
  INSURANCE_POLICY_BUCKET,
  INSURANCE_POLICY_MAX_BYTES,
  INSURANCE_TURNSTILE_ACTION,
} from "../../../netlify/functions/_shared/insurance-contract.mts";
import { readPublicAuthConfig, type PublicAuthConfig } from "./auth.js";

export interface InsurancePublicConfig extends PublicAuthConfig {
  readonly turnstileSiteKey: string;
}

interface SignedUpload {
  readonly path: string;
  readonly token: string;
  readonly receipt: string;
}

interface IntakeGateway {
  create(body: Record<string, unknown>): Promise<unknown>;
  upload(upload: SignedUpload, file: File): Promise<void>;
  confirm(input: {
    readonly leadId: string;
    readonly policyFileId: string;
    readonly receipt: string;
  }): Promise<void>;
}

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId: string): void;
}

declare global {
  interface Window { turnstile?: TurnstileApi; }
}

const PDF_MAGIC = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d]);
let turnstileLoader: Promise<TurnstileApi> | null = null;

export function readInsurancePublicConfig(
  environment: Record<string, string | boolean | undefined>,
): InsurancePublicConfig | null {
  const authConfig = readPublicAuthConfig(environment);
  const rawSiteKey = environment.VITE_TURNSTILE_SITE_KEY;
  if (authConfig === null && typeof rawSiteKey !== "string") return null;
  if (authConfig === null || typeof rawSiteKey !== "string" || rawSiteKey.trim().length < 8) {
    throw new Error("Configurazione pubblica del modulo assicurativo incompleta");
  }
  return { ...authConfig, turnstileSiteKey: rawSiteKey.trim() };
}

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function inspectPolicyPdf(file: File): Promise<{
  readonly byteLength: number;
  readonly mimeType: "application/pdf";
  readonly sha256: string;
}> {
  if (file.size < PDF_MAGIC.length || file.size > INSURANCE_POLICY_MAX_BYTES) {
    throw new Error("Il PDF deve avere una dimensione massima di 10 MB.");
  }
  if (file.type !== "" && file.type !== "application/pdf") {
    throw new Error("È consentito esclusivamente un file PDF.");
  }
  const bytes = await file.arrayBuffer();
  const beginning = new Uint8Array(bytes, 0, PDF_MAGIC.length);
  if (!PDF_MAGIC.every((byte, index) => beginning[index] === byte)) {
    throw new Error("Il file selezionato non contiene l’intestazione di un PDF.");
  }
  return { byteLength: file.size, mimeType: "application/pdf", sha256: await sha256Hex(bytes) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function createInsuranceIntakeGateway(
  config: InsurancePublicConfig,
  fetchFn: typeof fetch = fetch,
): IntakeGateway {
  const client = createClient(config.supabaseUrl, config.publishableKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  return {
    async create(body): Promise<unknown> {
      const response = await fetchFn("/api/insurance-intake", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json() as unknown;
      if (!response.ok) {
        const code = isRecord(payload) && isRecord(payload.error) && typeof payload.error.code === "string"
          ? payload.error.code : "INSURANCE_REQUEST_FAILED";
        if (code === "INTAKE_RATE_LIMITED") throw new Error("Troppe richieste ravvicinate. Attendi e riprova.");
        if (code === "TURNSTILE_VERIFICATION_FAILED") throw new Error("Verifica di sicurezza scaduta. Riprova.");
        throw new Error("La richiesta non è stata acquisita. Riprova tra poco.");
      }
      if (!isRecord(payload) || !isRecord(payload.data)) throw new Error("Risposta del servizio non valida.");
      return payload.data;
    },
    async upload(upload, file): Promise<void> {
      const { error } = await client.storage.from(INSURANCE_POLICY_BUCKET).uploadToSignedUrl(
        upload.path,
        upload.token,
        file,
        { contentType: "application/pdf", cacheControl: "0", upsert: false },
      );
      if (error !== null) throw new Error("La richiesta è stata registrata, ma il PDF non è arrivato. Contattaci prima di riprovare.");
    },
    async confirm(input): Promise<void> {
      const response = await fetchFn("/api/insurance-policy-upload-confirmation", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });
      if (response.ok) return;
      const payload = await response.json() as unknown;
      const code = isRecord(payload) && isRecord(payload.error) && typeof payload.error.code === "string"
        ? payload.error.code : "POLICY_CONFIRMATION_FAILED";
      if (code === "POLICY_UPLOAD_REJECTED") {
        throw new Error("Il file caricato non coincide con il PDF selezionato ed è stato rifiutato.");
      }
      if (code === "POLICY_UPLOAD_NOT_READY") {
        throw new Error("Il PDF è arrivato, ma la verifica non è ancora conclusa. Non caricarlo di nuovo: contattaci.");
      }
      throw new Error("Il PDF è arrivato, ma la ricevuta tecnica non è stata completata. Non caricarlo di nuovo: contattaci.");
    },
  };
}

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile !== undefined) return Promise.resolve(window.turnstile);
  if (turnstileLoader !== null) return turnstileLoader;
  turnstileLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      if (window.turnstile === undefined) reject(new Error("Turnstile non disponibile"));
      else resolve(window.turnstile);
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile non caricato")), { once: true });
    document.head.append(script);
  });
  return turnstileLoader;
}

function setFormsBusy(forms: readonly HTMLFormElement[], busy: boolean): void {
  forms.forEach((form) => form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>("input, select, button")
    .forEach((control) => { control.disabled = busy; }));
}

function formValue(form: HTMLFormElement, name: string): FormDataEntryValue | null {
  return new FormData(form).get(name);
}

export async function mountInsuranceIntake(options: {
  readonly callbackForm: HTMLFormElement;
  readonly policyForm: HTMLFormElement;
  readonly turnstileRoot: HTMLElement;
  readonly outcome: HTMLElement;
  readonly intro: HTMLElement;
  readonly environment?: Record<string, string | boolean | undefined>;
  readonly gateway?: IntakeGateway;
}): Promise<void> {
  const forms = [options.callbackForm, options.policyForm];
  setFormsBusy(forms, true);
  let config: InsurancePublicConfig | null;
  try { config = readInsurancePublicConfig(options.environment ?? import.meta.env); } catch {
    config = null;
  }
  if (config === null) {
    setFormsBusy(forms, true);
    options.intro.textContent = "Il modulo sicuro non è ancora collegato in questo ambiente: nessun dato viene trasmesso.";
    options.turnstileRoot.textContent = "Collegamento cloud non attivo.";
    return;
  }

  const gateway = options.gateway ?? createInsuranceIntakeGateway(config);
  let token: string | null = null;
  let turnstile: TurnstileApi;
  let widgetId: string;
  try {
    turnstile = await loadTurnstile();
    widgetId = turnstile.render(options.turnstileRoot, {
      sitekey: config.turnstileSiteKey,
      action: INSURANCE_TURNSTILE_ACTION,
      theme: "light",
      size: "flexible",
      callback: (value: string) => { token = value; },
      "expired-callback": () => { token = null; },
      "error-callback": () => { token = null; },
    });
  } catch {
    setFormsBusy(forms, true);
    options.turnstileRoot.textContent = "Verifica di sicurezza non disponibile. Ricarica la pagina.";
    return;
  }
  setFormsBusy(forms, false);

  const resetChallenge = (): void => {
    token = null;
    turnstile.reset(widgetId);
  };
  const showOutcome = (tone: "success" | "error", title: string, message: string): void => {
    options.outcome.dataset.tone = tone;
    options.outcome.replaceChildren();
    const strong = document.createElement("strong");
    const span = document.createElement("span");
    strong.textContent = title;
    span.textContent = message;
    options.outcome.append(strong, span);
    options.outcome.hidden = false;
  };
  const submit = async (form: HTMLFormElement, kind: "callback" | "policy_review"): Promise<void> => {
    if (!form.reportValidity()) return;
    if (token === null) {
      showOutcome("error", "Verifica richiesta", "Completa la verifica di sicurezza e riprova.");
      return;
    }
    setFormsBusy(forms, true);
    options.outcome.hidden = true;
    try {
      if (kind === "callback") {
        const phone = String(formValue(form, "phone") ?? "");
        const preferredWindow = String(formValue(form, "time") ?? "");
        const preferredChannel = formValue(form, "whatsapp") === null ? "phone" : "whatsapp";
        await gateway.create({ kind, phone, preferredWindow, preferredChannel, turnstileToken: token });
        form.reset();
        showOutcome("success", "Richiesta ricevuta", "Un operatore autorizzato userà il numero soltanto per ricontattarti nella fascia scelta.");
      } else {
        const email = String(formValue(form, "email") ?? "");
        const candidate = formValue(form, "policy");
        if (!(candidate instanceof File)) throw new Error("Seleziona il PDF della polizza.");
        const file = await inspectPolicyPdf(candidate);
        const result = await gateway.create({ kind, email, file, turnstileToken: token });
        if (!isRecord(result) || result.kind !== "policy_review"
          || typeof result.leadId !== "string" || typeof result.policyFileId !== "string"
          || !isRecord(result.upload)
          || typeof result.upload.path !== "string" || typeof result.upload.token !== "string"
          || typeof result.upload.receipt !== "string") {
          throw new Error("Risposta del servizio non valida.");
        }
        await gateway.upload({
          path: result.upload.path,
          token: result.upload.token,
          receipt: result.upload.receipt,
        }, candidate);
        await gateway.confirm({
          leadId: result.leadId,
          policyFileId: result.policyFileId,
          receipt: result.upload.receipt,
        });
        form.reset();
        showOutcome("success", "Polizza ricevuta e verificata", "Dimensione, impronta e formato PDF coincidono. Il documento resta separato dai preventivi e in attesa della scansione malware.");
      }
    } catch (cause) {
      showOutcome("error", "Invio non completato", cause instanceof Error ? cause.message : "Riprova tra poco.");
    } finally {
      resetChallenge();
      setFormsBusy(forms, false);
    }
  };

  options.callbackForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void submit(options.callbackForm, "callback");
  });
  options.policyForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void submit(options.policyForm, "policy_review");
  });
}

export { INSURANCE_INTAKE_CONSENT_TEXT };
